import { useState } from 'react';
import type { Language } from '../../api/useLanguage';
import styles from './RulesDropdown.module.css';

type Mode = 'default' | 'weapon';

type Props = {
  lang: Language;
  onLangChange: (l: Language) => void;
};

const RULES: Record<Mode, Record<Language, { title: string; steps: string[] }>> = {
  default: {
    en: {
      title: 'How to play',
      steps: [
        'You are secretly assigned a target.',
        'Eliminate your target by touching them and saying "you are dead, [name]" — they must hear it.',
        'If you see a kill happen, shout "Assasin!" or "Murder!" to challenge it. The victim can then decline the kill request and the hunter must try again.',
        'Once your target confirms an elimination in the app, you inherit their next target.',
        'The last person standing wins.',
        "Awards also go to the top killer (most kills) and the longest survivor with zero kills (god's favorite).",
        'If you are eliminated, confirm it honestly. Disputes can be settled by the moderator.',
      ],
    },
    no: {
      title: 'Slik spiller du',
      steps: [
        'Du får hemmelig tildelt et offer.',
        'Eliminer offeret ditt ved å berøre dem og si "du er død, [navn]" — de må høre det.',
        'Hvis du ser et drap skje, rop "Assasin!" eller "Mord!" for å ugyldiggjøre det. Offeret kan da avvise drepeforespørselen og morderen må prøve igjen.',
        'Når offeret ditt bekrefter elimineringen i appen, arver du deres neste mål.',
        'Den siste som gjenstår vinner.',
        'Det kåres også en seriemorder (flest drap) og en lengst overlevende uten drap (gods favorite).',
        'Hvis du blir eliminert, bekreft det ærlig. Uenigheter løses av moderator.',
      ],
    },
  },
  weapon: {
    en: {
      title: 'Weapon mode',
      steps: [
        'You are secretly assigned a target and a weapon.',
        'You must use your weapon exactly as described to eliminate your target — they must experience it.',
        "After the kill, tell your target how you did it if it wasn't obvious.",
        'Once your target confirms, you inherit their next target and their weapon.',
        'The last person standing wins.',
        "Awards also go to the top killer and the longest survivor with zero kills (god's favorite).",
        'Witness rule: if you see a kill attempt, open the app and tap "Report attempted murder". Name the hunter and the target — the weapon is filled in automatically.',
        'The accused receives a notification and can accept the arrest or contest it with a reason.',
        'If contested, the moderator decides: arrest the hunter, confirm the kill, or dismiss the report.',
        'An arrested player is out for the rest of the game.',
        'Tip: your weapon is always shown on your screen. You can suggest weapons before the game — the moderator approves them.',
      ],
    },
    no: {
      title: 'Våpenmodus',
      steps: [
        'Du får hemmelig tildelt et offer og et våpen.',
        'Du må bruke våpenet nøyaktig slik det er beskrevet for å eliminere offeret — de må oppleve det.',
        'Etter drapet, fortell offeret hvordan du gjorde det hvis det ikke var åpenbart.',
        'Når offeret bekrefter, arver du deres neste mål og deres våpen.',
        'Den siste som gjenstår vinner.',
        'Det kåres også en seriemorder og en lengst overlevende uten drap (gods favorite).',
        'Vitneregel: hvis du ser et drapsforsøk, åpne appen og trykk "Rapporter drapsforsøk". Oppgi hvem som angrep og hvem som ble angrepet — våpenet fylles inn automatisk.',
        'Den anklagede mottar et varsel og kan godta arrest eller bestride med en begrunnelse.',
        'Ved uenighet avgjør moderator: arrest, godkjenn drapet, eller avvis rapporten.',
        'En arrestert spiller er ute for resten av spillet.',
        'Tips: våpenet ditt vises alltid på skjermen. Du kan foreslå våpen før spillet starter — moderator godkjenner dem.',
      ],
    },
  },
};

export function RulesDropdown({ lang, onLangChange }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('default');

  const content = RULES[mode][lang];

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>
          {lang === 'no' ? 'Ny i spillet? Les reglene' : 'New to the game? Read the rules'}
        </span>
        <span className={styles.chevron}>{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.controls}>
            <div className={styles.tabs}>
              <button
                type="button"
                className={`${styles.tab} ${mode === 'default' ? styles.tabActive : ''}`}
                onClick={() => setMode('default')}
              >
                {lang === 'no' ? 'Standard' : 'Default'}
              </button>
              <button
                type="button"
                className={`${styles.tab} ${mode === 'weapon' ? styles.tabActive : ''}`}
                onClick={() => setMode('weapon')}
              >
                {lang === 'no' ? 'Våpenmodus' : 'Weapon mode'}
              </button>
            </div>

            <div className={styles.langToggle}>
              <button
                type="button"
                className={`${styles.langBtn} ${lang === 'en' ? styles.langActive : ''}`}
                onClick={() => onLangChange('en')}
              >
                EN
              </button>
              <button
                type="button"
                className={`${styles.langBtn} ${lang === 'no' ? styles.langActive : ''}`}
                onClick={() => onLangChange('no')}
              >
                NO
              </button>
            </div>
          </div>

          <h3 className={styles.ruleTitle}>{content.title}</h3>
          <ol className={styles.steps}>
            {content.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
